/**
 * MODIFIED: 2025-01-29 - refactor/task-1.5-dashboard-performance
 *
 * ARCHITECTURE: WebSocket Implementation
 * PURPOSE: Pure WebSocket transport layer with connection lifecycle management
 *
 * NEW PATTERNS IMPLEMENTED:
 * - State Machine: Formal connection states with transition validation (6 states)
 * - Reconnection Strategy: Exponential backoff with jitter (1s → 16s max)
 * - Heartbeat: PING/PONG with 30-second interval and 5-second timeout
 * - Security: Authentication via URL query parameter, validated by backend decorator
 *
 * KEY FEATURES:
 * - Connection state machine prevents invalid transitions
 * - Automatic reconnection with configurable max attempts
 * - Socket state validation to detect desync issues
 * - Event-based architecture for clean separation of concerns
 *
 * RELATED FILES:
 * - src/lib/services/websocket/websocket.state.svelte.ts
 * - src/lib/services/websocket/websocket.actions.svelte.ts
 */

// WebSocketTransport.ts
/**
 * Pure WebSocket transport layer
 *
 * Responsibilities:
 * - Raw WebSocket connection management (connect, disconnect, send, receive)
 * - Connection lifecycle (open, close, error events)
 * - Heartbeat / ping-pong mechanism
 * - Automatic reconnection with exponential backoff
 * - Connection state tracking
 *
 * Does NOT handle:
 * - Message routing or business logic
 * - Authentication (passed in as config)
 * - State management (emits events for consumers)
 * - Table subscriptions or data processing
 */

import { browser } from '$app/environment';
import { createLogger } from '$lib/utils/logger';
import type { WebSocketConfig } from '$lib/types/websocket/connection.types';
import { safeValidateMessage } from '$lib/types/websocket/schemas';

const log = createLogger('ws-transport');

/**
 * Connection State Machine
 *
 * Valid state transitions:
 * - DISCONNECTED → CONNECTING
 * - CONNECTING → CONNECTED | FAILED | DISCONNECTED
 * - CONNECTED → DISCONNECTING | RECONNECTING | FAILED
 * - DISCONNECTING → DISCONNECTED
 * - RECONNECTING → CONNECTING | FAILED | DISCONNECTED
 * - FAILED → DISCONNECTED | RECONNECTING
 *
 * This state machine prevents invalid transitions like:
 * - Connecting when already connected
 * - Multiple concurrent connections
 * - Reconnecting without proper disconnection
 */
export enum ConnectionState {
	DISCONNECTED = 'DISCONNECTED',
	CONNECTING = 'CONNECTING',
	CONNECTED = 'CONNECTED',
	DISCONNECTING = 'DISCONNECTING',
	RECONNECTING = 'RECONNECTING',
	FAILED = 'FAILED'
}

/**
 * Valid state transition map
 */
const VALID_TRANSITIONS: Record<ConnectionState, ConnectionState[]> = {
	[ConnectionState.DISCONNECTED]: [ConnectionState.CONNECTING],
	[ConnectionState.CONNECTING]: [
		ConnectionState.CONNECTED,
		ConnectionState.FAILED,
		ConnectionState.DISCONNECTED
	],
	[ConnectionState.CONNECTED]: [
		ConnectionState.DISCONNECTING,
		ConnectionState.RECONNECTING,
		ConnectionState.FAILED
	],
	[ConnectionState.DISCONNECTING]: [ConnectionState.DISCONNECTED],
	[ConnectionState.RECONNECTING]: [
		ConnectionState.CONNECTING,
		ConnectionState.FAILED,
		ConnectionState.DISCONNECTED
	],
	[ConnectionState.FAILED]: [ConnectionState.DISCONNECTED, ConnectionState.RECONNECTING]
};

/**
 * WebSocket transport events
 */
export interface TransportEvents {
	open: () => void;
	close: (event: CloseEvent) => void;
	error: (error: Event) => void;
	message: (data: any) => void;
	reconnecting: (attempt: number) => void;
	reconnected: () => void;
	maxReconnectFailed: () => void;
}

/**
 * Transport connection options
 */
export interface TransportOptions {
	url: string;
	authToken?: string;
	authType?: string;
	heartbeatInterval?: number;
	pongTimeout?: number;
	maxReconnectAttempts?: number;
	baseDelay?: number;
	maxDelay?: number;
}

/**
 * Transport connection state
 */
export interface TransportState {
	connected: boolean;
	connecting: boolean;
	reconnecting: boolean;
	reconnectAttempts: number;
	lastHeartbeat?: number;
	error?: string;
}

/**
 * Pure WebSocket transport layer
 * Handles only connection, reconnection, and raw message I/O
 */
export class WebSocketTransport {
	private socket: WebSocket | null = null;
	private options: Required<TransportOptions>;
	private eventHandlers: Partial<TransportEvents> = {};

	// Connection state machine
	private connectionState: ConnectionState = ConnectionState.DISCONNECTED;

	// Legacy connection state (maintained for backward compatibility)
	private state: TransportState = {
		connected: false,
		connecting: false,
		reconnecting: false,
		reconnectAttempts: 0
	};

	// Timers
	private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
	private pongTimer: ReturnType<typeof setTimeout> | null = null;
	private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

	// Reconnection config
	private preventReconnect: boolean = false;
	private readonly jitterFactor = 0.3; // 30% jitter

	constructor(options: TransportOptions) {
		this.options = {
			url: options.url,
			authToken: options.authToken ?? '',
			authType: options.authType ?? 'Bearer',
			heartbeatInterval: options.heartbeatInterval ?? 30000, // 30 seconds
			pongTimeout: options.pongTimeout ?? 5000, // 5 seconds
			maxReconnectAttempts: options.maxReconnectAttempts ?? 5,
			baseDelay: options.baseDelay ?? 1000, // 1 second
			maxDelay: options.maxDelay ?? 30000 // 30 seconds
		};
	}

	/**
	 * Validate state transition
	 *
	 * @param from - Current state
	 * @param to - Target state
	 * @returns true if transition is valid, false otherwise
	 */
	private validateStateTransition(from: ConnectionState, to: ConnectionState): boolean {
		const validTargets = VALID_TRANSITIONS[from] || [];
		return validTargets.includes(to);
	}

	/**
	 * Set connection state with validation
	 *
	 * Ensures all state transitions are valid according to the state machine.
	 * Invalid transitions are logged as errors and the state is set to FAILED.
	 *
	 * @param newState - Target state
	 */
	private setState(newState: ConnectionState): void {
		const currentState = this.connectionState;

		// Validate transition
		if (!this.validateStateTransition(currentState, newState)) {
			log.error(
				`❌ Invalid state transition: ${currentState} → ${newState} - Forcing FAILED state for safety`
			);

			// Force safe state
			this.connectionState = ConnectionState.FAILED;
			this.syncLegacyState();
			return;
		}

		log.debug(`✅ State transition: ${currentState} → ${newState}`);
		this.connectionState = newState;
		this.syncLegacyState();
	}

	/**
	 * Sync legacy state flags with connection state machine
	 *
	 * Maintains backward compatibility with existing code that uses
	 * the legacy state.connected, state.connecting flags.
	 */
	private syncLegacyState(): void {
		this.state.connected = this.connectionState === ConnectionState.CONNECTED;
		this.state.connecting = this.connectionState === ConnectionState.CONNECTING;
		this.state.reconnecting = this.connectionState === ConnectionState.RECONNECTING;
	}

	/**
	 * Validate socket state matches connection state
	 *
	 * Detects state desync issues between our state machine and actual WebSocket.
	 *
	 * @returns true if states are consistent, false if desync detected
	 */
	private validateSocketState(): boolean {
		if (!this.socket) {
			// No socket is valid for DISCONNECTED and FAILED states
			return (
				this.connectionState === ConnectionState.DISCONNECTED ||
				this.connectionState === ConnectionState.FAILED
			);
		}

		// Check socket.readyState consistency
		const socketState = this.socket.readyState;

		switch (this.connectionState) {
			case ConnectionState.CONNECTED:
				return socketState === WebSocket.OPEN;

			case ConnectionState.CONNECTING:
			case ConnectionState.RECONNECTING:
				return socketState === WebSocket.CONNECTING || socketState === WebSocket.OPEN;

			case ConnectionState.DISCONNECTING:
				return socketState === WebSocket.CLOSING || socketState === WebSocket.CLOSED;

			case ConnectionState.DISCONNECTED:
			case ConnectionState.FAILED:
				return socketState === WebSocket.CLOSED;

			default:
				return false;
		}
	}

	/**
	 * Register event handler
	 */
	on<K extends keyof TransportEvents>(event: K, handler: TransportEvents[K]): void {
		this.eventHandlers[event] = handler;
	}

	/**
	 * Remove event handler
	 */
	off<K extends keyof TransportEvents>(event: K): void {
		delete this.eventHandlers[event];
	}

	/**
	 * Get current transport state
	 */
	getState(): TransportState {
		return { ...this.state };
	}

	/**
	 * Check if connected
	 */
	isConnected(): boolean {
		return this.state.connected && this.socket?.readyState === WebSocket.OPEN;
	}

	/**
	 * Connect to WebSocket server
	 */
	async connect(): Promise<void> {
		if (!browser) {
			log.warn('🔌 Cannot connect: not in browser environment');
			return;
		}

		// Validate current state allows connection
		if (this.connectionState !== ConnectionState.DISCONNECTED) {
			log.warn(`🔌 Cannot connect from state: ${this.connectionState}`);
			return;
		}

		// Validate socket state if socket exists
		if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
			log.warn('🔌 Previous socket still active, cleaning up...');
			this.disconnect(true);
			await new Promise((resolve) => setTimeout(resolve, 100));
		}

		// Transition to CONNECTING state
		this.setState(ConnectionState.CONNECTING);
		this.state.error = undefined;

		return new Promise((resolve, reject) => {
			try {
				// Build WebSocket URL with token as query parameter
				// Backend expects: ?token={authToken}
				const wsUrl = this.options.authToken
					? `${this.options.url}?token=${encodeURIComponent(this.options.authToken)}`
					: this.options.url;

				log.info(`🔌 [1/3] Connecting to WebSocket`, {
					url: this.options.url,
					hasToken: !!this.options.authToken,
					tokenLength: this.options.authToken?.length || 0
				});

				log.info('🔌 [2/3] Creating WebSocket connection', {
					hasToken: !!this.options.authToken
				});

				this.socket = new WebSocket(wsUrl);

				// Connection timeout
				const connectionTimeout = setTimeout(() => {
					if (!this.state.connected) {
						log.error('🔌 Connection timeout', {
							url: this.options.url,
							attemptNumber: this.state.reconnectAttempts + 1,
							maxAttempts: this.options.maxReconnectAttempts,
							hasAuthToken: !!this.options.authToken
						});
						this.socket?.close();
						reject(new Error('Connection timeout'));
					}
				}, 5000);

				this.socket.onopen = () => {
					clearTimeout(connectionTimeout);
					log.info('🔌 [3/3] WebSocket opened, calling handleOpen');
					this.handleOpen();
					resolve();
				};

				this.socket.onclose = (event) => {
					clearTimeout(connectionTimeout);
					this.handleClose(event);
				};

				this.socket.onerror = (error) => {
					clearTimeout(connectionTimeout);
					this.handleError(error);
					reject(error);
				};

				this.socket.onmessage = (event) => {
					this.handleMessage(event);
				};
			} catch (error) {
				this.state.connecting = false;
				this.state.error = error instanceof Error ? error.message : String(error);
				log.error('Connection error', {
					error: error instanceof Error ? error.message : String(error),
					url: this.options.url,
					state: this.connectionState,
					reconnectAttempts: this.state.reconnectAttempts
				});
				reject(error);
			}
		});
	}

	/**
	 * Disconnect from WebSocket server
	 */
	disconnect(preventReconnect: boolean = false): void {
		log.info('Disconnecting...');

		// Transition to DISCONNECTING state
		this.setState(ConnectionState.DISCONNECTING);

		this.preventReconnect = preventReconnect;

		// Clear all timers
		this.stopHeartbeat();
		this.clearReconnectTimer();

		// Close socket
		if (this.socket) {
			this.socket.onclose = null; // Prevent reconnect
			this.socket.onerror = null;
			this.socket.onmessage = null;
			this.socket.onopen = null;

			if (
				this.socket.readyState === WebSocket.OPEN ||
				this.socket.readyState === WebSocket.CONNECTING
			) {
				this.socket.close(1000, 'Client disconnect');
			}

			this.socket = null;
		}

		// Transition to DISCONNECTED state
		this.setState(ConnectionState.DISCONNECTED);

		if (preventReconnect) {
			this.state.reconnectAttempts = 0;
		}
	}

	/**
	 * Send message through WebSocket
	 */
	send(data: any): boolean {
		if (!this.isConnected()) {
			log.warn('Cannot send: not connected');
			return false;
		}

		try {
			const message = typeof data === 'string' ? data : JSON.stringify(data);
			this.socket!.send(message);
			return true;
		} catch (error) {
			log.error('Failed to send WebSocket message', {
				error: error instanceof Error ? error.message : String(error),
				messageType: typeof data === 'string' ? 'string' : 'object',
				isConnected: this.isConnected(),
				socketState: this.socket?.readyState
			});
			return false;
		}
	}

	/**
	 * Update auth token
	 */
	updateAuth(authToken: string, authType: string): void {
		this.options.authToken = authToken;
		this.options.authType = authType;
	}

	/**
	 * Analyze validation errors and create smart summary
	 * Groups errors by pattern to avoid logging thousands of similar errors
	 */
	private analyzeValidationErrors(issues: any[], data: any): {
		summary: string;
		topErrors: Array<{ pattern: string; count: number; example: any }>;
		affectedFields: string[];
	} {
		// Group errors by field path pattern (remove array indices)
		const errorsByPattern = new Map<string, any[]>();

		issues.forEach((issue) => {
			// Convert path like ["regular_alerts", 0, "timestamp"] to "regular_alerts.[].timestamp"
			const pattern = issue.path
				.map((part: any) => (typeof part === 'number' ? '[]' : part))
				.join('.');

			if (!errorsByPattern.has(pattern)) {
				errorsByPattern.set(pattern, []);
			}
			errorsByPattern.get(pattern)!.push(issue);
		});

		// Get top 5 most common error patterns
		const topErrors = Array.from(errorsByPattern.entries())
			.map(([pattern, errors]) => ({
				pattern,
				count: errors.length,
				example: {
					message: errors[0].message,
					code: errors[0].code,
					expected: 'expected' in errors[0] ? errors[0].expected : undefined,
					received: 'received' in errors[0] ? errors[0].received : undefined,
					samplePath: errors[0].path.join('.')
				}
			}))
			.sort((a, b) => b.count - a.count)
			.slice(0, 5);

		// Create summary text
		const summary = topErrors
			.map(
				(e) =>
					`${e.count}× "${e.pattern}": ${e.example.message} (expected: ${e.example.expected}, received: ${e.example.received})`
			)
			.join(' | ');

		// Get unique affected field patterns
		const affectedFields = Array.from(errorsByPattern.keys());

		return {
			summary,
			topErrors,
			affectedFields
		};
	}

	/**
	 * Handle connection open
	 */
	private handleOpen(): void {
		log.info('🔌 [1/5] Connection opened, transitioning to CONNECTED');

		// Transition to CONNECTED state
		this.setState(ConnectionState.CONNECTED);

		// Reset reconnect attempts on successful connection
		if (this.state.reconnectAttempts > 0) {
			log.info(`🔌 Reconnected after ${this.state.reconnectAttempts} attempts`);
			this.state.reconnectAttempts = 0;
			this.eventHandlers.reconnected?.();
		}

		// AUTH message removed: Backend validates token via query parameter
		// before connection opens using @streamlined_websocket_auth decorator.
		// No post-connection authentication needed.
		// Token is sent in URL at line 329: `${this.options.url}?token=${encodeURIComponent(this.options.authToken)}`
		// Backend validates before WebSocket connection completes, making this redundant.
		log.info('🔌 [2/5] Auth already validated by backend (query param)');

		// CRITICAL: Subscribe to topics for real-time broadcasts
		// - "updates" for presence and state broadcasts
		// - "work_orders" for work order delta updates
		// - "alerts" for alert delta updates
		log.info('🔌 [3/5] Subscribing to real-time topics');
		this.send({
			type: 'SUBSCRIBE',
			data: {
				topics: ['updates', 'work_orders', 'alerts']
			}
		});
		log.info('🔌 [4/5] Subscribed to real-time topics');

		// Start heartbeat
		log.info('🔌 [5/5] Starting heartbeat, emitting open event');
		this.startHeartbeat();

		// Emit open event
		this.eventHandlers.open?.();
	}

	/**
	 * Handle connection close
	 */
		private handleClose(event: CloseEvent): void {
			log.info(`Connection closed: ${event.code} ${event.reason}`);

			// Determine target state based on how the close was triggered.
			// - Client-initiated or normal close (1000) → DISCONNECTED
			// - Server/error close (e.g. AUTH_ERROR → 1008) → FAILED so that
			//   subsequent transitions use FAILED → RECONNECTING, which is valid
			//   according to the state machine.
			const wasClientInitiated =
				this.connectionState === ConnectionState.DISCONNECTING ||
				this.preventReconnect ||
				event.code === 1000;

			if (wasClientInitiated) {
				this.setState(ConnectionState.DISCONNECTED);
			} else {
				this.setState(ConnectionState.FAILED);
			}

			// Stop heartbeat
			this.stopHeartbeat();

			// Emit close event
			this.eventHandlers.close?.(event);

			// Schedule reconnect if not prevented and this wasn't a normal close
			if (!this.preventReconnect && event.code !== 1000) {
				this.scheduleReconnect();
			}
		}

	/**
	 * Handle connection error
	 */
	private handleError(error: Event): void {
		log.error('Connection error:', error);

		// Transition to FAILED state
		this.setState(ConnectionState.FAILED);
		this.state.error = 'Connection error';

		// Emit error event
		this.eventHandlers.error?.(error);
	}

	/**
	 * Handle incoming message
	 */
	private handleMessage(event: MessageEvent): void {
		try {
			const data = JSON.parse(event.data);

			// Handle pong messages internally (skip validation for performance)
			if (data.type === 'PONG') {
				this.handlePong();
				return;
			}

			// Validate incoming message using Zod schemas
			const [validatedMessage, validationError] = safeValidateMessage(data);

			if (validationError) {
				// Smart error analysis - group errors by pattern instead of logging each one
				const errorAnalysis = this.analyzeValidationErrors(validationError.issues, data);

				log.error('❌ Message validation failed:', {
					messageType: data.type || 'unknown',
					type: data.type,
					errorCount: validationError.issues.length,
					// Smart summary instead of full error list
					summary: errorAnalysis.summary,
					// Top 5 most common errors
					topErrors: errorAnalysis.topErrors,
					// Affected field patterns
					affectedFields: errorAnalysis.affectedFields,
					// Sample of first 3 errors for debugging
					sampleErrors: validationError.issues.slice(0, 3).map((e) => ({
						path: e.path.join('.'),
						message: e.message,
						code: e.code,
						...('expected' in e && { expected: e.expected }),
						...('received' in e && { received: e.received })
					})),
					// Full error list available but collapsed
					allErrors: validationError.issues.map((e) => ({
						path: e.path.join('.'),
						message: e.message,
						code: e.code,
						...('expected' in e && { expected: (e as any).expected }),
						...('received' in e && { received: (e as any).received }),
						...('validation' in e && { validation: (e as any).validation })
					})),
					fullMessageData: data,
					prettyJSON: JSON.stringify(data, null, 2).substring(0, 1000)
				});

				// Emit error event for monitoring
				this.eventHandlers.error?.(
					new Event('error', {
						cancelable: false
					})
				);
				return; // Drop invalid messages to prevent downstream errors
			}


			// Emit validated message to handlers
			this.eventHandlers.message?.(validatedMessage);
		} catch (error) {
			// 🔍 ENHANCED ERROR LOGGING - Capture actual error details
			const messageSize = event?.data?.length || 0;
			const errorDetails = {
				errorType: error?.constructor?.name || 'Unknown',
				errorMessage: error instanceof Error ? error.message : String(error),
				errorStack: error instanceof Error ? error.stack?.substring(0, 500) : undefined,
				messageSizeBytes: messageSize,
				messageSizeMB: (messageSize / 1024 / 1024).toFixed(2),
				errorString: JSON.stringify(error),
				rawError: error
			};

			// Removed raw console.error - logger.error is sufficient
			log.error('🚨 [STAGE 1] Message parse/validation FAILED:', errorDetails);
		}
	}

	/**
	 * Start heartbeat mechanism
	 */
	private startHeartbeat(): void {
		this.stopHeartbeat();

		this.heartbeatTimer = setInterval(() => {
			if (this.isConnected()) {
				// Removed log.debug('Sending PING') - too noisy
				this.send({ type: 'PING' });
				this.state.lastHeartbeat = Date.now();

				// Set pong timeout
				this.pongTimer = setTimeout(() => {
					log.warn('PONG timeout - connection may be stale');
					this.disconnect(false); // Will trigger reconnect
				}, this.options.pongTimeout);
			}
		}, this.options.heartbeatInterval);
	}

	/**
	 * Stop heartbeat mechanism
	 */
	private stopHeartbeat(): void {
		if (this.heartbeatTimer) {
			clearInterval(this.heartbeatTimer);
			this.heartbeatTimer = null;
		}

		if (this.pongTimer) {
			clearTimeout(this.pongTimer);
			this.pongTimer = null;
		}
	}

	/**
	 * Handle PONG response
	 */
	private handlePong(): void {
		// Removed log.debug('Received PONG') - too noisy

		if (this.pongTimer) {
			clearTimeout(this.pongTimer);
			this.pongTimer = null;
		}
	}

	/**
	 * Schedule reconnection attempt
	 */
	private scheduleReconnect(): void {
		if (this.preventReconnect) {
			log.info('Reconnect prevented');
			return;
		}

		if (this.state.reconnectAttempts >= this.options.maxReconnectAttempts) {
			log.error('Max reconnect attempts reached', {
				attempts: this.state.reconnectAttempts,
				maxAttempts: this.options.maxReconnectAttempts,
				url: this.options.url,
				lastError: this.state.error
			});
			this.setState(ConnectionState.FAILED);
			this.eventHandlers.maxReconnectFailed?.();
			return;
		}

		// Transition to RECONNECTING state
		this.setState(ConnectionState.RECONNECTING);
		this.state.reconnectAttempts++;

		// Calculate delay with exponential backoff and jitter
		const exponentialDelay = Math.min(
			this.options.baseDelay * Math.pow(2, this.state.reconnectAttempts - 1),
			this.options.maxDelay
		);

		const jitter = exponentialDelay * this.jitterFactor * (Math.random() * 2 - 1);
		const delay = exponentialDelay + jitter;

		log.info(
			`Reconnecting in ${Math.round(delay)}ms (attempt ${this.state.reconnectAttempts}/${this.options.maxReconnectAttempts})`
		);

		this.eventHandlers.reconnecting?.(this.state.reconnectAttempts);

		this.reconnectTimer = setTimeout(() => {
			this.reconnectTimer = null;
			this.connect().catch((error) => {
				log.error('Reconnect failed:', error);
				this.scheduleReconnect(); // Try again
			});
		}, delay);
	}

	/**
	 * Clear reconnect timer
	 */
	private clearReconnectTimer(): void {
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}
	}

	/**
	 * Cleanup transport resources
	 */
	cleanup(): void {
		log.info('Cleaning up transport');
		this.disconnect(true);
		this.eventHandlers = {};
	}
}
